import { useState } from "react";
import { Link } from "react-router-dom";
import { Archive, ArrowUpRight, RotateCcw } from "lucide-react";
import { api, type Site } from "../lib/api";
import { forgePreviewUrl, siteHost, siteUrl } from "../lib/domains";
import { siteDisplayState } from "../lib/siteLifecycle";
import SiteThumbnail from "./SiteThumbnail";
import CancelBuildButton from "./CancelBuildButton";

/** Site identity is stable across every edit, build and publication. */
export function canonicalSites(sites: Site[]): Site[] {
  const byId = new Map<string, Site>();
  for (const site of sites) {
    const previous = byId.get(site.id);
    if (!previous || site.updatedAt >= previous.updatedAt) byId.set(site.id, site);
  }
  return [...byId.values()];
}

function ArchiveSiteButton({ site, onChanged }: { site: Site; onChanged: (site: Site) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const archived = !!site.archivedAt;
  async function change() {
    setBusy(true); setError(null);
    try { onChanged(await api.archiveSite(site.id, !archived)); }
    catch { setError("The workspace could not be updated. Try again."); }
    finally { setBusy(false); }
  }
  return <>
    <button type="button" className="btn" disabled={busy} onClick={() => void change()}
      title={archived ? "Show this site in your workspace again. Its live website is unchanged." : "Hide this site from your workspace. Its live website and history stay available."}>
      {archived ? <RotateCcw size={14} aria-hidden="true" /> : <Archive size={14} aria-hidden="true" />}
      {busy ? "Saving…" : archived ? "Restore to workspace" : "Archive"}
    </button>
    {error && <p role="alert">{error}</p>}
  </>;
}

export default function SiteGallery({ sites, onChanged, onRefresh, showOwner = false }: {
  sites: Site[]; onChanged: (site: Site) => void; onRefresh: () => void; showOwner?: boolean;
}) {
  return <div className="studio-site-grid">
    {canonicalSites(sites).map(site => {
      const previewBuildId = site.previewBuildId ?? site.liveBuildId;
      const state = siteDisplayState(site);
      return <article className="studio-site-card" key={site.id}>
        <SiteThumbnail site={site} className="site-preview-tile" />
        <div className="site-card-body">
          <div className="row between">
            <div><h2>{siteHost(site.subdomain)}</h2><p>Updated {new Date(site.updatedAt).toLocaleDateString()}</p></div>
            <span className={`badge ${state === "active" ? "good" : state === "building" ? "warn" : ""}`}>{state === "active" ? "Live" : state === "draft" ? "Draft" : state.replace("-", " ")}</span>
          </div>
          {showOwner && <p className="muted" style={{ overflowWrap: "anywhere" }}>Owner {site.ownerUserId}</p>}
          <div className="site-card-actions">
            {state === "building" ? <>
              {site.pendingBuildId && <Link to={`/sites/${site.id}/builds/${site.pendingBuildId}`} className="btn primary">Watch progress</Link>}
              <CancelBuildButton site={site} onCancelled={onRefresh} />
            </> : previewBuildId ? <>
              <Link to={`/sites/${site.id}/editor`} className="btn primary">Edit site</Link>
              <a href={forgePreviewUrl(previewBuildId)} target="_blank" rel="noreferrer" className="btn">Preview <ArrowUpRight size={14} aria-hidden="true" /></a>
              {state === "active" && <a href={siteUrl(site.subdomain)} target="_blank" rel="noreferrer" className="btn">View live <ArrowUpRight size={14} aria-hidden="true" /></a>}
            </> : <Link to={`/sites/${site.id}`} className="btn primary">Open site</Link>}
            <Link to={`/sites/${site.id}/builds`} className="btn">Version history</Link>
            <Link to={`/sites/${site.id}`} className="btn">Manage site</Link>
            <ArchiveSiteButton site={site} onChanged={onChanged} />
          </div>
        </div>
      </article>;
    })}
  </div>;
}
